import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useCallback, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOrderQueryFn } from '../../graphql/query/getOrder';
import { QUERIES } from '../../utils/constants';
import { rawString } from '../Language/Language';
import type { OrderTokenType } from '../../graphql/query/getRetailerSettings';
import { useWorkflowState } from '../../workflow/hooks';
import type { LoginSubmitData } from '../../layouts/Login/LoginLayout';
import {
  Login as LoginComponent,
  type LoginFieldType,
  type LoginTexts,
} from '@repo/pdx-ui/Login';
import { StepHeader } from '../StepHeader/StepHeader';
import { GiftSubSection } from './SubSections';

type LoginSchemaType = Record<OrderTokenType, z.ZodTypeAny>;

// Centralized validation messages
const validationMessages = {
  ORDER_NUMBER: {
    required: rawString({
      langKey: 'Global.login.ORDER_NUMBER.requiredError',
    }),
  },
  EMAIL: {
    required: rawString({
      langKey: 'Global.login.EMAIL.requiredError',
    }),
    invalid: rawString({
      langKey: 'Global.login.email.validationError',
    }),
  },
  ZIPCODE: {
    required: rawString({
      langKey: 'Global.login.ZIPCODE.requiredError',
    }),
  },
} as const;

export const LoginSchema: LoginSchemaType = {
  ORDER_NUMBER: z.string().trim().min(1, {
    error: validationMessages.ORDER_NUMBER.required,
  }),
  EMAIL: z.preprocess(
    (input) => {
      if (typeof input === 'string') {
        return input.trim().toLowerCase();
      }
      return input;
    },
    z.email({
      error: (issue) => {
        // Check the processed input for emptiness
        if (issue.input === undefined || issue.input === '') {
          return validationMessages.EMAIL.required;
        }
        return validationMessages.EMAIL.invalid;
      },
    }),
  ),
  ZIPCODE: z.string().trim().min(1, {
    error: validationMessages.ZIPCODE.required,
  }),
};

export const createLoginSchema = (orderTokens: OrderTokenType[]) =>
  z.object(
    orderTokens.reduce((acc, key) => {
      if (!key) return acc;
      return {
        [key]: LoginSchema[key],
        ...acc,
      };
    }, {} as LoginSchemaType),
  );

export interface LoginProps {
  onSubmit: (data: LoginSubmitData) => void;
  fields: OrderTokenType[];
  isCentered?: boolean;
}

export const Login = ({ onSubmit, fields, isCentered }: LoginProps) => {
  const navigate = useNavigate();
  const workflowState = useWorkflowState();
  const { retailer, features, requestId } = workflowState ?? {};
  const loginSchema = createLoginSchema(fields);
  type LoginSchemaType = z.infer<typeof loginSchema>;
  const methods = useForm<LoginSchemaType>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const params = useSearch({ strict: false });
  const orderParam = params?.order;
  const email = params?.email;
  const orderNumber = params?.orderNumber;
  const order = (orderParam ?? orderNumber)?.toString();

  const { data, isLoading, isError, refetch } = useQuery({
    enabled: false,
    retry: false,
    queryKey: [QUERIES.GET_ORDER],
    queryFn: () => {
      const values = methods.watch();
      const orderTokens = Object.entries(values).map(([key, value]) => ({
        key: key as OrderTokenType,
        value: value as string,
      }));
      return getOrderQueryFn({
        input: { orderTokens },
        skipEligibility: Boolean(requestId),
      });
    },
  });

  const { formState, register } = methods;
  const { errors, isValid } = formState;

  const redirectToItemSelection = useCallback(() => {
    navigate({
      to: '/$retailer/item-selection',
      search: (prev) => {
        const newSearch = { ...prev };
        delete newSearch.retailer;
        return newSearch;
      },
      params: { retailer: retailer ?? 's' },
    });
  }, [navigate, retailer]);

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (isLoading) return;
    const response = await refetch();
    const order = response.data?.order;
    const submittedRequests = response.data?.submittedRequests;
    if (order) {
      onSubmit({
        order,
        loginContext: {
          email: typeof data.EMAIL === 'string' ? data.EMAIL : undefined,
          orderNumber:
            typeof data.ORDER_NUMBER === 'string'
              ? data.ORDER_NUMBER
              : undefined,
          zipCode: typeof data.ZIPCODE === 'string' ? data.ZIPCODE : undefined,
        },
        submittedRequests: submittedRequests ?? [],
      });

      redirectToItemSelection();
    }
  });

  useEffect(() => {
    if (order && fields.includes('ORDER_NUMBER')) {
      methods.setValue('ORDER_NUMBER', order);
    }

    if (email && fields.includes('EMAIL')) {
      methods.setValue('EMAIL', email);
    }
    // eslint-disable-next-line exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      if (data.order) {
        onSubmit({
          loginContext: {
            orderNumber: order,
          },
          order: data.order,
          submittedRequests: data.submittedRequests,
        });
        redirectToItemSelection();
      }
    }
  }, [data, onSubmit, order, redirectToItemSelection]);

  const redirectToGift = () => {
    navigate({
      to: '/$retailer/gift-login',
      params: { retailer: retailer ?? 's' },
      search: true,
    });
  };

  if (!fields || fields.length === 0) return null;

  // Map OrderTokenType to LoginFieldType
  const loginFields: LoginFieldType[] = fields.filter(
    (field): field is LoginFieldType =>
      field === 'EMAIL' || field === 'ZIPCODE' || field === 'ORDER_NUMBER',
  );

  // Create texts object using rawString
  const texts: LoginTexts = {
    title: rawString({ langKey: 'Global.login.title' }) ?? 'Login',
    subtitle: rawString({ langKey: 'Global.login.description' }) ?? '',
    submitButton:
      rawString({ langKey: 'Global.login.submitButton' }) ?? 'Submit',
    submitErrorMessage:
      rawString({ langKey: 'Global.login.submitErrorMessage' }) ??
      'An error occurred',
    policyDescription:
      rawString({ langKey: 'Global.login.policyDescription' }) ?? '',
    policyUrl: rawString({ langKey: 'Global.login.policyUrl' }) ?? '',
    help: rawString({ langKey: 'Global.help' }) ?? 'Help',
    info: rawString({ langKey: 'Global.info' }) ?? 'Information',
    close: rawString({ langKey: 'Global.close' }) ?? 'Close',
    fieldLabel: (field: LoginFieldType) =>
      rawString({ langKey: `Global.login.${field}` }) ?? field,
    fieldHelp: (field: LoginFieldType) =>
      rawString({ langKey: `Global.login.help.${field}` }) ?? '',
  };

  // Render header using StepHeader component
  const renderHeader = () => (
    <StepHeader
      title={{ langKey: 'Global.login.title' }}
      subtitle={{ langKey: 'Global.login.description' }}
    />
  );

  // Build array of subsections to render
  const renderSubsections: Array<() => ReactNode> = [];
  if (features?.isGiftLoginEnabled) {
    renderSubsections.push(() => <GiftSubSection onChange={redirectToGift} />);
  }

  return (
    <LoginComponent
      fields={loginFields}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      isValid={isValid}
      isError={isError}
      isCentered={isCentered}
      methods={methods}
      texts={texts}
      renderHeader={renderHeader}
      renderSubsections={
        renderSubsections.length > 0 ? renderSubsections : undefined
      }
    />
  );
};
